import {YalwaButtonBase} from './button_base.js';

const has_element_internals = !!(window.ElementInternals && 'attachInternals' in HTMLElement.prototype);

export class YalwaButton extends YalwaButtonBase {
	
	static formAssociated = true;
	
	static get observedAttributes() {
		return [...super.observedAttributes, 'name', 'value', 'form', 'type'];
	}
	
	constructor() {
		super();
		this._internals = has_element_internals ? this.attachInternals() : null; // Required for form association
		this._form_proxy = null; // fallback for old browsers
		this.initialize_template();
		this.addEventListener('click', this.handle_click);
	}
	
	handle_click(event) {
        if (this.getAttribute('type') === 'submit') {
            if (this.form) {
                this.form.requestSubmit();
            }
        }
    }
	
	initialize_template() {
		// Only create the template if the shadow root is empty.
		// If DSD was used, the shadow root will already have children.
		if (!this.shadowRoot.firstChild) {
			const template = document.createElement('template');
			template.innerHTML = `
				<button class="button" part="button" type="button">
					<slot name="icon_left" part="icon_left"></slot>
					<slot part="label"></slot> <!-- Default slot for text -->
					<slot name="icon_right" part="icon_right"></slot>
				</button>
			`;
			this.shadowRoot.appendChild(template.content.cloneNode(true));
		}
		this.core_element = this.shadowRoot.querySelector('button');
		if (this.hasAttribute('autofocus')) {
			this.core_element.setAttribute('autofocus', '');
		}
	}
	
	connectedCallback() {
		if (!has_element_internals) {
			this.ensure_form_proxy();
			this.sync_form_proxy();
		}
		super.connectedCallback();
	}
	
	attributeChangedCallback(name, old_value, new_value) {
		super.attributeChangedCallback?.(name, old_value, new_value);
		if (!has_element_internals && (name === 'name' || name === 'value' || name === 'disabled' || name === 'form')) {
			this.sync_form_proxy();
		}
	}
	
	disconnectedCallback() {
		if (this._form_proxy?.parentNode) {
			this._form_proxy.parentNode.removeChild(this._form_proxy);
		}
	}
	
	render() {
		super.render();
		const $button = jQuery(this.core_element);
		// sync simple attributes that map directly to button attributes
		$button.attr('type', this.getAttribute('type') || 'button');
		if (this.name) {
			$button.attr('name', this.name);
		}
		if (this.value) {
			$button.attr('value', this.value);
		}
		$button.prop('disabled', this.disabled);
	}
	
	get name() {
		return this.getAttribute('name');
	}
	
	set name(value) {
		this.setAttribute('name', value);
	}
	
	get value() {
		return this.getAttribute('value');
	}
	
	set value(value) {
		this.setAttribute('value', value);
	}
	
	get form() {
		return this._internals?.form ?? (this._form_proxy?.form ?? null);
	}
	
	ensure_form_proxy() {
		if (this._form_proxy) {
			return;
		}
		const input = document.createElement('input');
		input.type = 'hidden';
		input.style.display = 'none';
		input.setAttribute('aria-hidden', 'true');
		
		const id = this.getAttribute('form');
		const target_form = id ? document.getElementById(id) : this.closest('form');
		(target_form ?? this).appendChild(input);
		
		this._form_proxy = input;
	}
	
	sync_form_proxy() {
		if (!this._form_proxy) {
			return;
		}
		const id = this.getAttribute('form');
		const target_form = id ? document.getElementById(id) : this.closest('form');
		if (target_form && this._form_proxy.parentNode !== target_form) {
			target_form.appendChild(this._form_proxy);
		}
		const name = this.getAttribute('name');
		if (name) {
			this._form_proxy.name = name;
			this._form_proxy.value = this.getAttribute('value') ?? '';
			this._form_proxy.disabled = this.hasAttribute('disabled');
		} else {
			this._form_proxy.removeAttribute('name'); // not submittable without a name
		}
	}
}

if (!customElements.get('yalwa-button')) {
    customElements.define('yalwa-button', YalwaButton);
}
